import { Trans, useTranslation } from 'react-i18next';
import Loading from '../../components/loading';
import PropTypes from 'prop-types';
import React from 'react';
import { Link } from 'react-router-dom';
import { Label, Table } from 'semantic-ui-react';
import useAppContext from '../../context/useAppContext';
import { WeezHeader } from '@weezevent/nacre';

export function urlPrefix(eventId, organizationId) {
    return `/ticket/O${organizationId}/E${eventId}`;
}

export default function QuotasGroups({ inventoryId, renderInventorySelector, quotas }) {
    const [{ event, organization }] = useAppContext();

    const prefix = urlPrefix(event.id, organization.id);

    function addQuotasToGeneralAdmission(generalAdmissionId) {
        return `${prefix}/seating/quotas/inventory/${inventoryId}/generaladmission/${generalAdmissionId}/edit`;
    }

    function addQuotasToContingent(contingentId) {
        return `${prefix}/seating/quotas/inventory/${inventoryId}/contingent/${contingentId}/edit`;
    }

    function modifyQuotasForContingent(contingentId) {
        return `${prefix}/seating/quotas/inventory/${inventoryId}/contingent/${contingentId}/tabular`;
    }

    function modifyQuotasForGeneralAdmissionArea(generalAdmissionAreaId) {
        return `${prefix}/seating/quotas/inventory/${inventoryId}/generaladmission/${generalAdmissionAreaId}/tabulareg`;
    }

    function getModifyTranslation() {
        return <Trans i18nKey="ticket.common.action.edit">Modify</Trans>;
    }

    function compareAlphabetical(itemA, itemB) {
        if (itemA.name > itemB.name) {
            return 1;
        }

        if (itemA.name < itemB.name) {
            return -1;
        }

        return 0;
    }

    function getRows() {
        const { t } = useTranslation();
        let rows = quotas
            .sort((itemA, itemB) => {
                if (itemA.contingent && !itemB.contingent) {
                    return -1;
                }

                if (itemB.contingent && !itemA.contingent) {
                    return 1;
                }

                if ((itemA.contingent && itemB.contingent) || (itemA.generalAdmissionArea && itemB.generalAdmissionArea)) {
                    return compareAlphabetical(itemA, itemB);
                }

                return 0;
            })
            .map(row => {
                let isGeneralAdmission = !!row.generalAdmissionArea;
                let capacity = row.all === null ? t('ticket.common.unlimited') : row.all || 0;
                return (
                    <Table.Row key={row.contingent || row.generalAdmissionArea}>
                        <Table.Cell>
                            {isGeneralAdmission ? (
                                <Trans i18nKey="ticket.common.generalAdmittance">General Admission</Trans>
                            ) : (
                                <Trans i18nKey="ticket.common.contingent">Contingent</Trans>
                            )}
                        </Table.Cell>
                        <Table.Cell>{row.name}</Table.Cell>
                        <Table.Cell>
                            {row.booked || (isNaN(row.booked) ? '-' : '0')}
                            {row.bookingInProgress ? <Label>{`+${row.bookingInProgress}`}</Label> : ''}
                        </Table.Cell>
                        <Table.Cell>{capacity}</Table.Cell>
                        <Table.Cell>
                            {row.isGeneralAdmissionArea() ? (
                                <Link to={addQuotasToGeneralAdmission(row.id)}>
                                    <Trans i18nKey="ticket.common.action.add">Add</Trans>
                                </Link>
                            ) : (
                                <Link to={addQuotasToContingent(row.id)}>
                                    <Trans i18nKey="ticket.common.action.add">Add</Trans>
                                </Link>
                            )}
                        </Table.Cell>
                        <Table.Cell>
                            {row.isGeneralAdmissionArea() ? (
                                <Link to={modifyQuotasForGeneralAdmissionArea(row.id)}>{getModifyTranslation()}</Link>
                            ) : (
                                <Link to={modifyQuotasForContingent(row.id)}>{getModifyTranslation()}</Link>
                            )}
                        </Table.Cell>
                    </Table.Row>
                );
            });

        return rows;
    }

    function renderRows() {
        let rows = getRows();
        return rows;
    }

    function renderTotalRow() {
        let totalBooked = 0;
        let totalBooking = 0;
        let totalCapacity = 0;
        quotas.map(row => {
            totalBooked += row.booked;
            totalBooking += row.bookingInProgress;
            totalCapacity += row.all;
            return null;
        });
        return (
            <Table.Row key={'row-total'} className={'row-total'}>
                <Table.Cell className={'total-header'}>
                    <Trans i18nKey="ticket.common.total">Total</Trans>
                </Table.Cell>
                <Table.Cell className={'no-border'} />
                <Table.Cell>
                    {totalBooked > 0 ? totalBooked : '-'}
                    {totalBooking > 0 ? <Label>{`+${totalBooking}`}</Label> : ''}
                </Table.Cell>
                <Table.Cell>{totalCapacity > 0 ? totalCapacity : '-'}</Table.Cell>
                <Table.Cell />
                <Table.Cell className={'no-border'} />
            </Table.Row>
        );
    }

    function renderContent() {
        return (
            <Table basic="very" celled className="quota-groups-table">
                <Table.Header>
                    <Table.Row>
                        <Table.HeaderCell width={2}>
                            <Trans i18nKey="ticket.common.type">Type</Trans>
                        </Table.HeaderCell>
                        <Table.HeaderCell width={6}>
                            <Trans i18nKey="ticket.chart.name">Name</Trans>
                        </Table.HeaderCell>
                        <Table.HeaderCell>
                            <Trans i18nKey="ticket.common.sold">Sold</Trans>
                        </Table.HeaderCell>
                        <Table.HeaderCell>
                            <Trans i18nKey="ticket.quotas.capacity">Capacity</Trans>
                        </Table.HeaderCell>
                        <Table.HeaderCell width={4}>
                            <Trans i18nKey="ticket.common.actions">Actions</Trans>
                        </Table.HeaderCell>
                        <Table.HeaderCell className={'no-border'} />
                    </Table.Row>
                </Table.Header>
                <Table.Body>
                    {renderRows()}
                    {renderTotalRow()}
                </Table.Body>
            </Table>
        );
    }

    if (!quotas) {
        return <Loading />;
    }

    return (
        <div>
            <WeezHeader title="Quotas" />
            <div className="header">
                {renderInventorySelector()}
                <p></p>
            </div>
            {renderContent()}
        </div>
    );
}
QuotasGroups.propTypes = {
    inventoryId: PropTypes.string.isRequired,
    rates: PropTypes.object.isRequired,
    renderInventorySelector: PropTypes.func.isRequired,
    quotas: PropTypes.object.isRequired
};
